import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getPrimaryImage } from '../../../utils/imageUtils';
import { formatItemPrice, isRangePriced } from '../../../utils/pricingUtils';

const ServiceCard = ({ 
  service, 
  onEdit, 
  onDelete, 
  onDragStart, 
  onDragEnd,
  isDragging 
}) => {
  const [imageError, setImageError] = useState(false);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, service)}
      onDragEnd={onDragEnd}
      className={`bg-card border border-border rounded-xl p-4 md:p-5 lg:p-6 transition-all duration-250 ease-elegant hover:shadow-lg ${
        isDragging ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
      }`}
    >
      <div className="flex flex-col sm:flex-row gap-4 md:gap-5 lg:gap-6">
        <div className="flex-shrink-0">
          <div className="relative w-full sm:w-24 md:w-28 lg:w-32 h-24 md:h-28 lg:h-32 rounded-lg overflow-hidden bg-muted">
            {!imageError ? (
              <Image
                src={getPrimaryImage(service?.imageUrl)}
                alt={service?.imageAlt}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon name="ImageOff" size={32} color="var(--color-muted-foreground)" />
              </div>
            )}
            <button
              className="absolute top-2 left-2 p-1.5 bg-background/80 backdrop-blur-sm rounded-lg cursor-grab active:cursor-grabbing hover:bg-background transition-colors duration-250"
              aria-label="Drag to reorder"
            >
              <Icon name="GripVertical" size={16} color="var(--color-muted-foreground)" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold text-base md:text-lg lg:text-xl text-foreground truncate">
                {service?.name}
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground font-caption mt-1">
                {service?.category}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="ghost"
                size="icon"
                iconName="Edit2"
                iconSize={18}
                onClick={() => onEdit(service)}
                className="hover:bg-primary/10 hover:text-primary"
              />
              <Button
                variant="ghost"
                size="icon"
                iconName="Trash2"
                iconSize={18}
                onClick={() => onDelete(service?.id)}
                className="hover:bg-error/10 hover:text-error"
              />
            </div>
          </div>

          <p className="text-sm md:text-base text-foreground/80 font-caption line-clamp-2 mb-3">
            {service?.description}
          </p>

          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            <div className="flex items-center gap-2">
              <Icon name="ReceiptCent" size={16} color="var(--color-primary)" />
              <span className="font-caption font-semibold text-sm md:text-base text-foreground whitespace-nowrap">
                {formatItemPrice(service)}
              </span>
            </div>
            {isRangePriced(service) && (
              <span className="rounded-full bg-warning/10 px-2 py-1 text-xs text-warning font-caption">
                Price range
              </span>
            )}
            {service?.type === 'service' && (
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={16} color="var(--color-secondary)" />
                <span className="font-caption text-sm md:text-base text-muted-foreground whitespace-nowrap">
                  {service?.durationInMinutes} min
                </span>
              </div>
            )}
           
            <span className={`px-2 py-1 text-xs font-caption rounded-full flex items-center gap-1 ${
              service?.type === 'product'
                ? 'bg-secondary/10 text-secondary'
                : 'bg-accent/10 text-accent'
            }`}>
              <Icon name={service?.type === 'product' ? 'Package' : 'Sparkles'} size={12} />
              {service?.type === 'product' ? 'Product' : 'Service'}
            </span>

            {service?.isPopular && (
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-caption rounded-full flex items-center gap-1">
                <Icon name="Star" size={12} />
                Popular
              </span>
            )}

            {/* Stock Status for Products */}
            {service?.type === 'product' && service?.currentStock != null && (
              <div className="flex items-center gap-2">
                <Icon name="Package" size={16} color={service.isOutOfStock ? "var(--color-destructive)" : service.isLowStock ? "var(--color-warning)" : "var(--color-success)"} />
                <span className={`font-caption text-xs whitespace-nowrap ${
                  service.isOutOfStock ? 'text-destructive' : 
                  service.isLowStock ? 'text-warning' : 
                  'text-success'
                }`}>
                  {service.isOutOfStock ? 'Out of Stock' : 
                   service.isLowStock ? `Low Stock (${service.currentStock})` : 
                   `In Stock (${service.currentStock})`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;
